import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, nomenclature as nomApi, type Nomenclature } from '@/api/catalogs';
import {
  nomenclatureSpecifications,
  type NomenclatureSpecificationItemIn,
} from '@/api/nomenclatureSpecifications';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useConfirm } from '@/components/ConfirmProvider';

/**
 * Закладка «Комплектація» картки номенклатури (підфіча #3, alembic 0262).
 *
 * Повноцінне редагування складу набору ПРЯМО на картці:
 * - dropdown юрособи (per-org create/edit-право через usePermissionsByOrg) —
 *   склад набору per-org, картка tenant-scoped, тому юрособу обираємо тут;
 * - якщо для обраної юрособи є ASSEMBLY-специфікація цього товару — редагуємо
 *   її склад inline (компоненти + долі собівартості %, сума=100);
 * - якщо немає — кнопка «Створити склад набору» (inline create).
 *
 * Це закриває TECHDEBT read-only (день 28 ч.3): org-резолв через
 * usePermissionsByOrg('production.specification.manage') — патерн RefsPage/
 * NomenclatureSpecificationsPage (view = wide, create/edit = narrow per-org).
 */
const PERMISSION = 'production.specification.manage';

const newRow = (n: number): NomenclatureSpecificationItemIn => ({
  line_number: n,
  raw_material_nomenclature_id: '',
  quantity: '1',
  cost_percentage: null,
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Помилка збереження';
  }
  return String(e);
}

const sumPct = (items: NomenclatureSpecificationItemIn[]): number =>
  items.reduce(
    (acc, it) =>
      acc + (it.cost_percentage == null || it.cost_percentage === '' ? 0 : Number(it.cost_percentage)),
    0,
  );

export function NomenclatureKitTab({ nomenclatureId }: { nomenclatureId: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();

  // ── Юрособа: dropdown (повний для перегляду) + множина org з create/edit-правом.
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );
  const [orgId, setOrgId] = useState('');
  useEffect(() => {
    // Дефолт — перша юрособа з create-правом (щоб одразу можна редагувати).
    const firstEditable = perms.availableOrgs[0]?.id;
    const firstAny = orgList.data?.items[0]?.id;
    if (!orgId && (firstEditable || firstAny)) setOrgId(firstEditable ?? firstAny ?? '');
  }, [orgList.data, perms.availableOrgs, orgId]);
  const canManage = !!orgId && allowedOrgIds.has(orgId);

  // ── ASSEMBLY-специфікація цього товару для обраної юрособи.
  const listKey = ['nomenclature-kit', nomenclatureId, orgId];
  const list = useQuery({
    queryKey: listKey,
    queryFn: () =>
      nomenclatureSpecifications.list({
        product_nomenclature_id: nomenclatureId,
        organization_id: orgId,
        purpose: 'ASSEMBLY',
        limit: 50,
      }),
    enabled: !!orgId,
  });
  // Беремо активну (або першу) специфікацію.
  const spec = useMemo(() => {
    const items = list.data?.items ?? [];
    return items.find((s) => s.is_active && !s.deletion_mark) ?? items[0] ?? null;
  }, [list.data]);

  const detail = useQuery({
    queryKey: ['nomenclature-spec-detail', spec?.id],
    queryFn: () => nomenclatureSpecifications.get(spec!.id),
    enabled: !!spec,
  });

  // ── Резолв назв номенклатури.
  const nomQ = useQuery({ queryKey: ['nomenclature-all'], queryFn: () => nomApi.list({ limit: 1000 }) });

  // ── Локальний стан редагування складу.
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('Склад набору');
  const [items, setItems] = useState<NomenclatureSpecificationItemIn[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Завантаження складу у форму при вході в режим редагування / зміні спеки.
  useEffect(() => {
    if (detail.data) {
      setName(detail.data.name);
      setItems(
        detail.data.items.map((it) => ({
          line_number: it.line_number,
          raw_material_nomenclature_id: it.raw_material_nomenclature_id,
          quantity: it.quantity,
          uom_id: it.uom_id,
          comment: it.comment,
          cost_percentage: it.cost_percentage,
        })),
      );
    }
  }, [detail.data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['nomenclature-kit', nomenclatureId] });
    if (spec) qc.invalidateQueries({ queryKey: ['nomenclature-spec-detail', spec.id] });
  };

  const save = useMutation({
    mutationFn: () => {
      const payloadItems = items.map((it, i) => ({ ...it, line_number: i + 1 }));
      if (spec) {
        return nomenclatureSpecifications.update(spec.id, {
          name,
          purpose: 'ASSEMBLY',
          is_active: true,
          items: payloadItems,
        });
      }
      return nomenclatureSpecifications.create({
        organization_id: orgId,
        product_nomenclature_id: nomenclatureId,
        base_quantity: '1',
        name,
        purpose: 'ASSEMBLY',
        is_active: true,
        items: payloadItems,
      });
    },
    onSuccess: () => {
      setEditing(false);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(extractError(e)),
  });

  const total = sumPct(items);
  const pctOk = items.length > 0 && Math.abs(total - 100) < 0.005;

  const startCreate = () => {
    setName('Склад набору');
    setItems([newRow(1)]);
    setEditing(true);
    setError(null);
  };
  const startEdit = () => { setEditing(true); setError(null); };
  const addItem = () => setItems((arr) => [...arr, newRow(arr.length + 1)]);
  const removeItem = (i: number) =>
    setItems((arr) => arr.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })));
  const setItem = (i: number, patch: Partial<NomenclatureSpecificationItemIn>) =>
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomQ.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomQ.data]);

  return (
    <div>
      <p style={{ color: 'var(--muted)', maxWidth: 680 }}>
        Склад набору (комплекту) — це перелік компонентів і долі собівартості (%).
        Документ комплектації списує компоненти й оприбутковує набір, а
        розукомплектація розбирає набір назад за цими долями. Сума долей = 100%.
      </p>

      {/* Юрособа: склад набору per-org. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0 1rem' }}>
        <label style={{ color: 'var(--muted)' }}>Юрособа:</label>
        <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setEditing(false); }}>
          <option value="">—</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
          ))}
        </select>
        {orgId && !canManage && (
          <small style={{ color: '#64748b' }}>
            (немає прав редагувати у цій юрособі — лише перегляд)
          </small>
        )}
      </div>

      {!orgId && <div style={{ color: 'var(--muted)' }}>Оберіть юрособу.</div>}

      {orgId && list.isLoading && <div>Завантаження…</div>}

      {/* Немає складу + є право → кнопка створення. */}
      {orgId && !list.isLoading && !spec && !editing && (
        <div style={{ padding: '1rem', border: '1px dashed var(--border, #cbd5e1)', borderRadius: 6 }}>
          Склад набору для цієї юрособи ще не задано.{' '}
          {canManage ? (
            <button className="primary" onClick={startCreate}>+ Створити склад набору</button>
          ) : (
            <span style={{ color: 'var(--muted)' }}>Немає прав на створення.</span>
          )}
        </div>
      )}

      {/* Перегляд складу (не в режимі редагування). */}
      {orgId && spec && !editing && (
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.35rem' }}>
            <strong>{detail.data?.name ?? spec.name}</strong>
            {spec && !spec.is_active && (
              <span style={{ color: 'var(--muted)', fontSize: '0.85em' }}>(неактивна)</span>
            )}
            {canManage && (
              <button style={{ marginLeft: 'auto' }} onClick={startEdit}>✎ Редагувати</button>
            )}
          </div>
          <KitTable
            items={(detail.data?.items ?? []).map((it) => ({
              line_number: it.line_number,
              raw_material_nomenclature_id: it.raw_material_nomenclature_id,
              quantity: it.quantity,
              cost_percentage: it.cost_percentage,
            }))}
            readOnly
            nomName={nomName}
            nomOptions={nomQ.data?.items ?? []}
            onAdd={() => {}}
            onRemove={() => {}}
            onSet={() => {}}
          />
        </div>
      )}

      {/* Режим редагування складу inline. */}
      {orgId && editing && (
        <div>
          <div className="field" style={{ maxWidth: 360, marginBottom: '0.5rem' }}>
            <label>Назва складу набору</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <KitTable
            items={items}
            readOnly={false}
            nomName={nomName}
            nomOptions={nomQ.data?.items ?? []}
            onAdd={addItem}
            onRemove={removeItem}
            onSet={setItem}
          />
          <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontWeight: 600 }}>
              Разом доля:{' '}
              <span style={{ color: pctOk ? 'inherit' : '#dc2626' }}>
                {total.toLocaleString('uk-UA', { maximumFractionDigits: 2 })}%
              </span>
            </span>
            {!pctOk && items.length > 0 && (
              <small style={{ color: '#dc2626' }}>Сума долей має = 100%</small>
            )}
          </div>
          {error && <div className="error" style={{ marginTop: '0.5rem' }}>{error}</div>}
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
            <button
              className="primary"
              disabled={!pctOk || save.isPending}
              title={!pctOk ? 'Сума долей має = 100%' : undefined}
              onClick={() => save.mutate()}
            >
              Зберегти
            </button>
            <button
              onClick={() => {
                if (spec) { setEditing(false); setError(null); }
                else confirm('Скасувати створення складу?').then((ok) => { if (ok) { setEditing(false); setItems([]); setError(null); } });
              }}
            >
              Скасувати
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Таблиця складу (компонент + к-сть + доля %). readOnly або редагування.
function KitTable({
  items,
  readOnly,
  nomName,
  nomOptions,
  onAdd,
  onRemove,
  onSet,
}: {
  items: NomenclatureSpecificationItemIn[];
  readOnly: boolean;
  nomName: Map<string, string>;
  nomOptions: Nomenclature[];
  onAdd: () => void;
  onRemove: (i: number) => void;
  onSet: (i: number, patch: Partial<NomenclatureSpecificationItemIn>) => void;
}) {
  const qtyFmt = (v: string) => Number(v).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
  return (
    <>
      <table className="data-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th style={{ textAlign: 'left' }}>Компонент</th>
            <th style={{ width: 110, textAlign: 'right' }}>К-сть</th>
            <th style={{ width: 120, textAlign: 'right' }}>Доля %</th>
            {!readOnly && <th style={{ width: 40 }} />}
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                {readOnly ? (
                  nomName.get(it.raw_material_nomenclature_id) ?? it.raw_material_nomenclature_id
                ) : (
                  <select value={it.raw_material_nomenclature_id}
                          onChange={(e) => onSet(i, { raw_material_nomenclature_id: e.target.value })}>
                    <option value="">—</option>
                    {nomOptions.map((n) => (
                      <option key={n.id} value={n.id}>{n.code ? `${n.code} — ` : ''}{n.name}</option>
                    ))}
                  </select>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                {readOnly ? (
                  qtyFmt(it.quantity)
                ) : (
                  <input type="number" step="0.001" value={it.quantity}
                         style={{ width: 90, textAlign: 'right' }}
                         onChange={(e) => onSet(i, { quantity: e.target.value })} />
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                {readOnly ? (
                  it.cost_percentage == null ? '—' : `${Number(it.cost_percentage)}%`
                ) : (
                  <input type="number" step="0.01" min="0" max="100" placeholder="%"
                         value={it.cost_percentage ?? ''}
                         style={{ width: 90, textAlign: 'right' }}
                         onChange={(e) => onSet(i, { cost_percentage: e.target.value || null })} />
                )}
              </td>
              {!readOnly && (
                <td style={{ textAlign: 'center' }}>
                  <button title="Видалити" onClick={() => onRemove(i)}>×</button>
                </td>
              )}
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={readOnly ? 4 : 5} style={{ color: 'var(--muted)' }}>Компонентів немає.</td>
            </tr>
          )}
        </tbody>
      </table>
      {!readOnly && (
        <button onClick={onAdd} style={{ marginTop: '0.5rem' }}>+ Додати компонент</button>
      )}
    </>
  );
}
